import { Tab, TabList, TabPanel, Tabs } from "@medoma/components/Tabs";
import { Heading } from "@medoma/components/Heading/Heading";
import { Trans } from "@lingui/react/macro";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import styles from "./NewActivity.module.scss";
import { IconButton } from "@medoma/components/Button/Button";
import CrossIcon from "@medoma/components/icons/CrossIcon";
import { useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { usePatients } from "@/api/Patients";
import { patientStatusSchema, type IPatientId } from "@medoma/models/patients";
import * as Sentry from "@sentry/react";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { ControlledSelect } from "@medoma/components/Select/Select";
import { getPatientNameWithStatus } from "@/api/Patients";
import clsx from "clsx";
import {
  NO_PATIENT_SELECTED,
  type NewActivityOutletContext,
} from "./newActivityUtils";
import { useSelectedDateNoDefault } from "@/Utils/useSelectedDate";

const NewActivity = ({ inDialog = false }: { inDialog?: boolean }) => {
  const navigate = useNavigate();
  const { t } = useLingui();
  const patientIdFromParams = useParams().patientId;

  return (
    <article className={clsx(styles.newActivity, inDialog && styles.inDialog)}>
      {!inDialog ? (
        <header className={styles.header}>
          <Heading level="h1">
            <Trans>Skapa aktivitet</Trans>
          </Heading>
          <IconButton aria-label={t`Stäng`} onClick={() => navigate("..")}>
            <CrossIcon />
          </IconButton>
        </header>
      ) : null}
      <Tabs>
        <TabList>
          <Tab to="templates">
            <Trans>Mallar</Trans>
          </Tab>
          <Tab to="custom">
            <Trans>Anpassad</Trans>
          </Tab>
          <Tab to="retroactive">
            <Trans>Efterregistrering</Trans>
          </Tab>
        </TabList>
        <TabPanel>
          {patientIdFromParams ? (
            <TabPanelWithoutPatientSelector patientId={patientIdFromParams} />
          ) : (
            <TabPanelWithPatientSelector />
          )}
        </TabPanel>
      </Tabs>
    </article>
  );
};

const TabPanelWithoutPatientSelector = ({
  patientId,
}: {
  patientId: IPatientId;
}) => {
  const location = useLocation();
  const selectedDate = useSelectedDateNoDefault();

  // Selected date is not applicable when on the patients tab, so we always use today's date as default start date for new activities
  const defaultStartDate = location.pathname.startsWith(
    "/commandcenter/patients",
  )
    ? undefined
    : selectedDate;

  const outletContext: NewActivityOutletContext = {
    patientId,
    defaultStartDate,
  };

  return <Outlet context={outletContext} />;
};

const TabPanelWithPatientSelector = () => {
  const { t } = useLingui();
  const [patientId, setPatientId] = useState<IPatientId | undefined>(undefined);
  const location = useLocation();
  const selectedDate = useSelectedDateNoDefault();

  // Selected date is not applicable when on the patients tab, so we always use today's date as default start date for new activities
  const defaultStartDate = location.pathname.startsWith(
    "/commandcenter/patients",
  )
    ? undefined
    : selectedDate;

  const outletContext: NewActivityOutletContext = {
    patientId,
    defaultStartDate,
  };

  const showProspectAndAdmittedPatientsInSelectOptionsList =
    location.pathname.endsWith("/templates") ||
    location.pathname.endsWith("/custom");
  const showAdmittedAndDischargedPatientsInSelectOptionsList =
    location.pathname.endsWith("/retroactive");

  const {
    data: patients,
    isError,
    isPending,
    error,
  } = usePatients({
    statuses: showProspectAndAdmittedPatientsInSelectOptionsList
      ? [patientStatusSchema.enum.prospect, patientStatusSchema.enum.admitted]
      : showAdmittedAndDischargedPatientsInSelectOptionsList
        ? [
            patientStatusSchema.enum.admitted,
            patientStatusSchema.enum.discharged,
          ]
        : [],
  });

  if (isPending) {
    return <Loading message={t`Hämtar patienter`} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patienter.`} ${deducedError(error)}`}
      />
    );
  }

  return (
    <>
      <div className={styles.patientSelect}>
        <ControlledSelect
          label={t`Patient`}
          value={patientId ?? NO_PATIENT_SELECTED}
          onChange={(value) => {
            if (value === NO_PATIENT_SELECTED) {
              setPatientId(undefined);
            } else {
              setPatientId(value);
            }
          }}
        >
          <option value={NO_PATIENT_SELECTED}>
            <Trans>Ingen</Trans>
          </option>
          <optgroup label={t`Patienter`}>
            {patients.map((patient) => (
              <option key={patient.id} value={patient.id}>
                {getPatientNameWithStatus(patient, "full")}
              </option>
            ))}
          </optgroup>
        </ControlledSelect>
      </div>
      {/* Outlet must be given a key to force a re-render when the patientId changes
      so that the form is reset and uses the new patientId for creating activities */}
      <Outlet key={patientId} context={outletContext} />
    </>
  );
};

export { NewActivity };
